<script setup lang="ts">
import isEmail from 'validator/lib/isEmail'

type SubscribeResponse = {
  success?: boolean
  signedUp?: boolean
}

const email = ref('')
const firstName = ref('')
const lastName = ref('')

const pending = ref(false)
const res = ref<SubscribeResponse | null>(null)

// The result message clears itself again so the form doesn't keep reporting on
// a submission the visitor has long moved on from.
const resTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

watch(res, (value) => {
  if (resTimeout.value) {
    clearTimeout(resTimeout.value)
    resTimeout.value = null
  }

  if (value) {
    resTimeout.value = setTimeout(() => {
      res.value = null
    }, 10000)
  }
})

const emailIsValid = computed(() => isEmail(email.value))
const firstNameIsValid = computed(() => firstName.value.trim().length > 0)
const lastNameIsValid = computed(() => lastName.value.trim().length > 0)

const isValid = computed(() => emailIsValid.value && firstNameIsValid.value && lastNameIsValid.value)

// A field only starts flagging itself once the visitor has typed something
// anywhere in the form — an untouched form shouldn't read as broken.
const hasInput = computed(() => !!(email.value || firstName.value || lastName.value))

const invalidAttr = (fieldIsValid: boolean) => (hasInput.value && !fieldIsValid ? '' : null)

const message = computed(() => {
  if (!res.value) return null
  if (res.value.success) return 'newsletter.success'
  if (res.value.signedUp) return 'newsletter.alreadySubscribed'
  return 'newsletter.error'
})

const subscribe = async () => {
  if (!isValid.value || pending.value) return

  pending.value = true

  try {
    res.value = await $fetch<SubscribeResponse>('/api/mailchimp', {
      params: {
        email: email.value,
        firstName: firstName.value,
        lastName: lastName.value,
      },
    })
  } catch {
    res.value = { success: false }
  } finally {
    pending.value = false
  }

  if (res.value?.success) {
    email.value = ''
    firstName.value = ''
    lastName.value = ''
  }
}

onBeforeUnmount(() => {
  if (resTimeout.value) {
    clearTimeout(resTimeout.value)
    resTimeout.value = null
  }
})
</script>

<template>
  <form
    class="container-d px-1 my-3 text-md"
    novalidate
    @submit.prevent="subscribe"
  >
    <div class="grid gap-1 sm:grid-cols-2">
      <input
        v-model="email"
        type="email"
        name="email"
        autocomplete="email"
        :placeholder="$t('newsletter.email')"
        :aria-label="$t('newsletter.email')"
        :data-invalid="invalidAttr(emailIsValid)"
        class="newsletter-field sm:col-span-full"
      />

      <input
        v-model="firstName"
        type="text"
        name="firstName"
        autocomplete="given-name"
        :placeholder="$t('newsletter.firstName')"
        :aria-label="$t('newsletter.firstName')"
        :data-invalid="invalidAttr(firstNameIsValid)"
        class="newsletter-field"
      />

      <input
        v-model="lastName"
        type="text"
        name="lastName"
        autocomplete="family-name"
        :placeholder="$t('newsletter.lastName')"
        :aria-label="$t('newsletter.lastName')"
        :data-invalid="invalidAttr(lastNameIsValid)"
        class="newsletter-field"
      />
    </div>

    <button
      type="submit"
      :disabled="!isValid || pending"
      class="mt-1 button"
    >
      {{ pending ? $t('newsletter.submitting') : $t('newsletter.submit') }}
    </button>

    <p
      v-if="message"
      class="mt-1"
      aria-live="polite"
    >
      {{ $t(message) }}
    </p>
  </form>
</template>

<style scoped>
/* the fields sit on the same hairline the rest of the site separates rows with,
   so they read as part of the type rather than as boxes dropped onto it */
.newsletter-field {
  width: 100%;
  padding-bottom: calc(var(--unit--vertical) * 0.25);
  background: transparent;
  /* mobile-first, same as list-sep: small stroke on phones, full stroke from md */
  border-bottom: var(--stroke--width--sm) solid currentColor;
}

@media (min-width: 768px) {
  .newsletter-field {
    border-bottom-width: var(--stroke--width);
  }
}

.newsletter-field::placeholder {
  color: var(--color--gray);
}

.newsletter-field:focus {
  outline: none;
  border-bottom-color: var(--color--accent, var(--color--black));
}

.newsletter-field[data-invalid] {
  border-bottom-color: var(--color--gray);
}
</style>
